import { Controller } from "stimulus"
import { roundNumber, formatNumber } from "@react/quotes/form/helpers"

export default class extends Controller {
  static targets = [
    "quantity",
    "price",
    "vat",
    "importCoefficient",
    "importCoefficientOutput",
    "discount",
    "subtotalNotDiscounted",
    "subtotalDiscounted",
    "subtotal",
    "storeSubtotal",
    "storeVat",
    "updateTotals",
    "billingCategory"
  ]

  initialize () {
    this.calculate = this.calculate.bind(this)
  }

  // Give Cocoon time to insert the html
  connect() {
    setTimeout(this.calculate, 50)
  }

  calculate() {
    this._setVatRate()
    this._setVariables()
    this._storeTotals()
    this._calculateTotal()
    this._setImportCoefficient()
  }

  // A product owns its line's billing category, so the VAT rate only fills a
  // hole there. Without a product the rate is the only source, and drives the
  // field on every change.
  applyBillingCategory() {
    if (!this.hasBillingCategoryTarget) return

    const mappedId = this.vatTarget.selectedOptions[0]?.dataset.billingCategoryId

    if (this._hasProduct()) {
      if (this.billingCategoryTarget.value || !mappedId) return

      return this._setBillingCategory(mappedId)
    }

    mappedId ? this._setBillingCategory(mappedId) : this._clearBillingCategory()
  }

  // This method is a shortcut to create a new line when submiting the quantity
  // on a product line
  newLine(e) {
    if (e.key == "Enter") {
      e.preventDefault()
      e.stopPropagation()
      const button = document.querySelector("[data-association-insertion-node]")
      if (button) {
        button.click()
        setTimeout(() => {
          const newProductInput = document.querySelector(".row .autocomplete__input")
          if (newProductInput) newProductInput.focus()
        }, 300)
      }
    }
  }

  _setImportCoefficient() {
    if (!this.hasImportCoefficientOutputTarget || !this.hasImportCoefficientTarget) return
    this.importCoefficientOutputTarget.innerText = formatNumber(this.subtotal * this.importCoefficientTarget.value)
  }

  _setVatRate() {
    if (this.vatTarget.value) return

    if (document.querySelector("[name*='[site_id]'"))     this._setSiteVatRate()
    if (document.querySelector("[name*='[supplier_id]'")) this._setSupplierVatRate()

    if (this.vatTarget.value) setTimeout(() => this.applyBillingCategory(), 500)
  }

  _setSiteVatRate() {
    const dataElement = document.getElementById("asset-print")
    if (!dataElement) return

    const data          = JSON.parse(dataElement.innerText)
    const siteVatRateId = data.site.vat_rate?.id
    if (!siteVatRateId) return
    this.vatTarget.value = siteVatRateId
  }

  _setSupplierVatRate() {
    const data = document.getElementById("suppliers")
    if (!data) return

    const supplierInput = document.querySelector("input[name*='[supplier_id]']")

    const suppliers  = JSON.parse(data.dataset.reactProps).options
    const supplier   = suppliers.find(s => s.id == supplierInput.value)
    if (!supplier || !supplier.vatRateId) return

    this.vatTarget.value = supplier.vatRateId
  }

  _hasProduct() {
    return !!this.element.querySelector("input[name*='[product_id]']")?.value
  }

  _setBillingCategory(id) {
    // The option list is already client-scoped when billing_categories_per_client
    // is on, so a category the client may not use is never applied.
    const category = this._billingCategoryOptions().find(option => String(option.id) === String(id))
    if (!category) return

    this.billingCategoryTarget.dispatchEvent(new CustomEvent("combobox:update", { detail: category }))
  }

  _clearBillingCategory() {
    if (!this.billingCategoryTarget.value) return

    const container = this.billingCategoryTarget.closest("[data-react-class]")
    if (!container?.dataset.reactProps) return

    const props = JSON.parse(container.dataset.reactProps)
    props.value = null
    container.dataset.reactProps = JSON.stringify(props)
    reloadReact(container.parentElement)
  }

  _billingCategoryOptions() {
    const container = this.billingCategoryTarget.closest("[data-react-class]")
    if (!container?.dataset.reactProps) return []

    return JSON.parse(container.dataset.reactProps).options || []
  }

  _setVariables() {
    this.quantity              = parseFloat(this.quantityTarget.value) || 0
    this.price                 = parseFloat(this.priceTarget.value) || 0
    this.subtotalNotDiscounted = roundNumber(this.quantity * this.price)
    this.subtotalDiscounted    = roundNumber(this.subtotalNotDiscounted * this._discount() / 100)
    this.subtotal              = roundNumber(this.subtotalNotDiscounted - this.subtotalDiscounted)
  }

  _vat() {
    const selectedVat = this.vatTarget.selectedOptions[0]
    if(!selectedVat)                    return 0
    if(!selectedVat.dataset.percentage) return 0

    return parseFloat(selectedVat.dataset.percentage) / 100
  }

  _discount() {
    if (!this.hasDiscountTarget) return 0

    return parseFloat(this.discountTarget.value) || 0
  }

  _storeTotals() {
    if (this.hasSubtotalNotDiscountedTarget) {
      this.subtotalNotDiscountedTarget.innerText = formatNumber(this.subtotalNotDiscounted)
    }
    if (this.hasSubtotalDiscountedTarget) {
      this.subtotalDiscountedTarget.innerText = formatNumber(this.subtotalDiscounted * -1)
    }
    if(this.hasSubtotalTarget) {
      this.subtotalTarget.innerText = formatNumber(this.subtotal)
    }
    this.storeSubtotalTarget.value  = this.subtotal
    this.storeVatTarget.value       = this._vat()
  }

  _calculateTotal() {
    const event = new Event('calculate')
    this.updateTotalsTarget.dispatchEvent(event)
  }

  _setSiteVatRatePercentage() {
    const siteSelect = document.querySelector("select[name*='[site_id]']")
    if (!siteSelect) return

    const site = siteSelect.selectedOptions[0]
    if (!site) return

    const vatRateId = site.dataset.vat
    if (!vatRateId) return
  }
}
